// Presentation helpers for the catalog/tender views.
//
// SSR-safe: no `window`/`document` access. Uses Luxon for date formatting so
// we never call `new Date(string)` (forbidden per project convention).
import { DateTime } from 'luxon';
import { countryTimeZone, displayTimeZone } from '@tenders/shared';
import { countryConfig } from '../country';
import { CHILE_EDITION, HISPANIC_EDITION } from './hispanic-edition';

/**
 * The site's clock — where a «данные на …» stamp, a limit's reset time or a
 * one-time access end is read: `Asia/Tashkent` on UZ and KZ (no `timeZone` in
 * their config; this file hardcoded it before), `Europe/Madrid` on ES.
 */
export const SITE_TIME_ZONE = countryTimeZone(countryConfig);

/**
 * The zone a DEADLINE or numeric date is read in: UTC on UZ and KZ, exactly as
 * before, and `Europe/Madrid` on ES — PLACSP deadlines are Madrid wall clock
 * stored as UTC instants, so a lot closing at 00:30 on the 20th in Madrid
 * (22:30 UTC on the 19th) must print the 20th.
 */
const DATE_ZONE = displayTimeZone(countryConfig);

/**
 * A PLAIN decimal («33000.000000000000000000000000000000», «32000.5») loses its
 * fraction before any digit-stripping; anything else is returned as it came.
 *
 * Digit-stripping deletes the point and keeps the fraction's digits, so a
 * numeric(65,30) amount from a raw-SQL `::text` cast — the KZ «other lots of
 * this announcement» block, 2026-10-15 — printed as a 35-digit number. Only a
 * bare `digits.digits` string is touched: a space-grouped «1 234 567», a
 * «1.234.567» with dots as group separators or a «… сум» suffix keep the old
 * path exactly, so whole amounts format as they always did.
 */
function withoutDecimalFraction(value: string): string {
  const m = /^\s*(\d+)\.\d+\s*$/.exec(value);
  return m ? m[1]! : value;
}

/**
 * Format a raw UZS integer string with thin-space grouping, matching the m16
 * budget cells ("4 500 000 000"). Returns null when there's no budget.
 */
export function formatBudget(budgetUzs: string | null | undefined): string | null {
  if (!budgetUzs) return null;
  const numberLocale = countryConfig.currency.numberLocale;
  if (numberLocale) return formatLocaleAmount(budgetUzs, numberLocale);
  const digits = withoutDecimalFraction(budgetUzs).replace(/[^0-9]/g, '');
  if (!digits) return null;
  // Group thousands with a regular space (m16 uses spaced grouping).
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/**
 * A deployment whose amounts carry CENTS (`currency.numberLocale` — Spain, euros):
 * «1.234,56» / «4.500.000», grouped and separated by that locale through `Intl`.
 * Cents show only when there are some, so a whole budget reads as one. Grouping
 * is forced on four-digit numbers too («1.234», not CLDR-Spanish «1234»), the
 * way prices are printed on Spanish tender pages.
 *
 * Only ever reached when `numberLocale` is set — UZ and KZ never get here, and
 * `Intl` is safe for the locales that set it (Chrome ships `es`; it does not
 * ship `uz`, which is why the legacy formatter above avoids `Intl`).
 */
function formatLocaleAmount(value: string, numberLocale: string): string | null {
  const plain = value.trim();
  const n = /^-?\d+(\.\d+)?$/.test(plain) ? Number(plain) : Number(plain.replace(/[^0-9]/g, ''));
  if (!Number.isFinite(n) || plain.replace(/[^0-9]/g, '') === '') return null;
  const cents = Math.round(n * 100) % 100 !== 0;
  // `useGrouping: 'always'` is ES2023 Intl (Chrome 106+, Node 19+); the ES2022
  // lib types still say boolean, hence the cast.
  const options = {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
    useGrouping: 'always',
  } as unknown as Intl.NumberFormatOptions;
  return new Intl.NumberFormat(numberLocale, options).format(n);
}

/**
 * Magnitude suffixes, per locale, largest first.
 *
 * ⚠️ These are spelled out here instead of using ICU's `notation: 'compact'`
 * because **Chrome's ICU has no compact-decimal data for `uz`** and silently
 * falls back to SI prefixes. Every headline number on the Uzbek site rendered
 * as `72.7T so'm` / `570.8G so'm` — a period decimal and an untranslated `G`,
 * on the default locale of an Uzbek-language site.
 *
 * What makes this class of bug expensive is that it is **invisible from Node**:
 *
 *   node → new Intl.NumberFormat('uz', {notation:'compact'}).format(1.9e9)
 *          → "1,9 mlrd"     ✅ correct
 *   chrome→ same call                              → "1.9G"        ❌ shipped
 *
 * So it type-checks, it passes a unit test written against the Node runtime,
 * and the docstring that claimed «1,9 mlrd (uz)» was accurate — in the one
 * runtime no user has. Only the browser disagreed. `ru` and `uz-Cyrl` DO have
 * the data, which is why this looked fine on the Russian pages.
 *
 * An explicit table also makes the output identical in SSR, prerender and the
 * browser, so a compact number can never cause a hydration mismatch.
 */
// Threshold ladder, largest first — paired positionally with the per-locale
// suffix strings in `countryConfig.currency.compactUnits` (moved there behind
// the CountryConfig seam; see packages/shared/src/country/uz.ts).
const MAGNITUDE_THRESHOLDS = [1e12, 1e9, 1e6, 1e3] as const;

/**
 * Compact, localized budget headline — "1,9 млрд" (ru) / "1,9 mlrd" (uz) — for
 * the rail + mobile facts block, where the full 10-digit grouped number is
 * noisy. Returns null when there is no number. Never includes the currency word
 * — the caller appends сум / so'm, same contract as `formatBudget` (so the exact
 * grouped value stays available via a `title={formatBudget(value)}` tooltip).
 */
export function formatBudgetCompact(
  budgetUzs: string | null | undefined,
  locale: string,
): string | null {
  if (!budgetUzs) return null;

  // ⚠️ A plain decimal must be parsed as a NUMBER, not digit-stripped.
  //
  // This used to do `replace(/[^0-9]/g, '')` unconditionally, which deletes the
  // decimal point along with the spaces: "17419972.5" became "174199725" and
  // rendered as «174,2 млн» instead of «17,4 млн» — a silent 10x inflation, on
  // the price-reference page suppliers use to set bids. It fired whenever a
  // median landed on .5, i.e. on any even-sized sample.
  //
  // Digit-stripping is still the fallback, because callers also pass
  // space-grouped integers like "1 234 567" which Number() cannot read.
  const direct = Number(budgetUzs);
  if (Number.isFinite(direct) && budgetUzs.trim() !== '') return compactMagnitude(direct, locale);

  const digits = withoutDecimalFraction(budgetUzs).replace(/[^0-9]/g, '');
  if (!digits) return null;
  return compactMagnitude(Number(digits), locale);
}

/** Shared by every compact formatter — see the docstring above for why it is manual. */
function compactMagnitude(value: number, locale: string): string {
  // A locale with its own suffix table (ES 'es') uses it; any other keeps the old
  // rule — uz* → 'uz', anything else → 'ru'. Byte-identical on UZ (tables 'ru' +
  // 'uz') and KZ (table 'ru' only: 'kk' has always read it). A deployment without
  // the chosen table (a hardcoded 'ru' caller on ES) falls back to its default
  // locale's table instead of throwing.
  const tables = countryConfig.currency.compactUnits;
  const localeKey = Object.hasOwn(tables, locale) ? locale : locale.startsWith('uz') ? 'uz' : 'ru';
  const suffixes = (tables[localeKey] ?? tables[countryConfig.defaultLocale])!;
  // Chile's units are words, so a figure of exactly 1 takes the singular («1 millón»).
  // No such table anywhere else: UZ/KZ/ES print exactly what they did.
  const oneTables = countryConfig.currency.compactUnitsOne;
  const singular = oneTables
    ? (oneTables[localeKey] ?? oneTables[countryConfig.defaultLocale])
    : null;
  const units: [number, string][] = MAGNITUDE_THRESHOLDS.map((threshold, i) => [
    threshold,
    suffixes[i]!,
  ]);
  const abs = Math.abs(value);

  for (let i = 0; i < units.length; i++) {
    const [threshold] = units[i]!;
    if (abs < threshold) continue;

    // ⚠️ 999,999 must read "1 млн", not "1000 тыс.". Rounding to one decimal can
    // push the value over the NEXT unit's boundary, and ICU promotes in that
    // case — so we do too, or this fix would introduce a discrepancy on the
    // Russian pages that are correct today.
    const crosses = Math.abs(Number((value / threshold).toFixed(1))) >= 1000;
    const j = crosses && i > 0 ? i - 1 : i;
    const [t, s] = units[j]!;
    const figure = formatDecimal(value / t, locale);
    return `${figure} ${singular && figure === '1' ? (singular[j] ?? s) : s}`;
  }

  return formatDecimal(value, locale);
}

/**
 * One decimal place, comma-separated. Both site locales use a comma.
 *
 * ⚠️ Not `Intl.NumberFormat` either. Chrome's gap in `uz` is not limited to
 * compact notation — it has no number symbols for the locale at all and falls
 * back to root, which uses a period:
 *
 *   node   → Intl.NumberFormat('uz').format(72.7) → "72,7"
 *   chrome → same call                            → "72.7"
 *
 * Caught only because the replacement for the compact-notation bug was checked
 * in a real browser rather than in the test runner: the suffixes came out right
 * and the separator was still wrong. `ru` is unaffected, as before.
 */
function formatDecimal(n: number, _locale: string): string {
  const rounded = Math.round(n * 10) / 10;
  const s = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return s.replace('.', ',');
}

/**
 * Uzbek month names, because Chrome does not have them.
 *
 * ⚠️ `Intl.DateTimeFormat('uz', { month: 'long' })` returns **"M09"** in Chrome
 * — the CLDR root placeholder — so every date on the Uzbek site rendered as
 * "4 M08 2026". Catalog cards, tender detail, «E'lon sanasi», all of it, on the
 * site's DEFAULT locale. Verified on production.
 *
 *   node   → Intl.DateTimeFormat('uz', {month:'long'}) → "sentabr"
 *   chrome → same call                                 → "M09"
 *
 * Same runtime gap as the number formatting above and found the same way: only
 * by rendering in a browser. Luxon delegates month names to Intl, so `MMMM` was
 * never going to survive this — the data simply is not in Chrome.
 *
 * `ru` is unaffected and stays on Luxon, which matters: Russian month names are
 * grammatically inflected, and the "d MMMM yyyy" phrase needs the genitive
 * ("21 июля 2026", not "21 июль 2026"). Hardcoding those would mean shipping a
 * second, worse copy of data ICU already has correct.
 */
const UZ_MONTHS = [
  'yanvar',
  'fevral',
  'mart',
  'aprel',
  'may',
  'iyun',
  'iyul',
  'avgust',
  'sentabr',
  'oktabr',
  'noyabr',
  'dekabr',
];

/**
 * Format an ISO deadline as a localized "1 сентября 2026" / "1 sentabr 2026".
 * `locale` defaults to 'ru' (the app's DEFAULT_LOCALE) when omitted. Returns
 * null when absent/invalid.
 */
export function formatDeadline(
  deadline: string | null | undefined,
  locale?: string,
): string | null {
  if (!deadline) return null;
  const dt = DateTime.fromISO(deadline, { zone: DATE_ZONE }).setLocale(locale ?? 'ru');
  if (!dt.isValid) return null;
  if ((locale ?? 'ru').startsWith('uz')) {
    return `${dt.day} ${UZ_MONTHS[dt.month - 1]} ${dt.year}`;
  }
  // Spanish writes the prepositions: «1 de septiembre de 2026». Chrome ships `es`
  // month names, so Luxon/Intl is safe here.
  if (locale === 'es') return dt.toFormat("d 'de' MMMM 'de' yyyy");
  return dt.toFormat('d MMMM yyyy');
}

/** «27.09.2026» on UZ and KZ, as always; «27/09/2026» on Spain, where a dotted date
 *  reads as foreign (2026-10-21); «27-09-2026» on Chile, the way Chileans (and
 *  Mercado Público) write it. A fixed pattern either way, never `Intl`. */
const NUMERIC_DATE =
  countryConfig.code === 'es' ? 'dd/MM/yyyy' : CHILE_EDITION ? 'dd-MM-yyyy' : 'dd.MM.yyyy';

/**
 * Format an ISO date as a bare numeric `DD.MM.YYYY`.
 *
 * Deliberately locale-INDEPENDENT (a fixed `toFormat` pattern, never
 * `toLocaleString`/`Intl`): Chrome ships no `uz` CLDR data, so anything that
 * resolves month names or numeric patterns through the locale renders as the
 * root fallback there while passing every test in Node — the same trap
 * `formatDeadline`/`formatDateTime` above work around with `UZ_MONTHS`. A dotted
 * numeric date reads the same in ru, uz and kk, so there is nothing to localize.
 * Returns null when absent/invalid.
 */
export function formatDateNumeric(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const dt = DateTime.fromISO(iso, { zone: DATE_ZONE });
  return dt.isValid ? dt.toFormat(NUMERIC_DATE) : null;
}

/**
 * ⚠️ `tenders.bid_security_uzs` is a TWO-UNIT column despite its name: some
 * sources publish the bid security as an ABSOLUTE so'm amount, others as a
 * PERCENT of the budget. Both land in the same numeric column, untagged.
 *
 * Measured on prod (2026-09-11), live lots only (`deadline > now()`):
 *
 *   value       rows   sources                                what it is
 *   ─────────────────────────────────────────────────────────────────────────
 *   0.2 … 3      628   etender-uzex 511, xt-xarid 97,         a PERCENT
 *                      ebirja 20                              (0,2/0,4/0,5/1/
 *                                                              1,5/1,8/2/3)
 *   1 500 …    1 270   uzex-xarid 650, tender-mc 536,         so'm
 *                      xt-xarid 82, cooperation-auction 2
 *   100 … 1 499    0   —                                      —
 *
 * The two populations do not overlap and are 15× apart: across the WHOLE table
 * (482,514 rows, 6,318 with a value) the smallest absolute amount is 1,500 and
 * the 100–1,499 band is empty.
 *
 * ⚠️ This is not an inference from the numbers — it was checked against the
 * SOURCE PAGES of eight live lots on 2026-09-11, and every source labels the
 * unit that our column throws away:
 *
 *   etender-uzex 509130/509593/508123 → «Zakalat miqdori: 1% / 2% / 3%
 *                                        (lotning boshlang'ich summasidan)»
 *   ebirja 1180 / 1257                → «Yetkazib beruvchining garov summasi
 *                                        3% / 1%»
 *   xt-xarid 8597559 / 8545090        → «-Тип задатка: процент», value 3 / 0.5
 *   xt-xarid 8636900                  → «Сумма залога: 181 928 350.8» — a SUM,
 *                                        and exactly 3.00% of its budget
 *
 * So xt-xarid alone publishes both units, with its own discriminator field, and
 * `deposit_sum` flattens them into one column. 100 is the split point — well
 * clear of the largest rate seen (3) and of the smallest sum seen (1,500).
 *
 * Rendering the raw number produced «Обеспечение: 1» on a 1,2-млрд lot (the
 * client screenshot that prompted this), and worse for the fractional rates:
 * `formatBudget` digit-strips, so 0.5 rendered as «05».
 *
 * ⚠️ Since 2026-09-29 the API writes those rates as so'm (`round(budget × rate
 * / 100)`, `services/api/src/modules/tenders/bid-security.ts`) and sends the
 * rate itself as `bidSecurityPct`. The ≤ 100 reading below stays for rows not
 * yet converted; `publishedPct` is what keeps «3 %» on the converted ones.
 */
const BID_SECURITY_PERCENT_CEILING = 100;

export interface BidSecurity {
  /** The published rate, when the source gave a percent. `null` for an amount. */
  percent: number | null;
  /** Absolute so'm — published outright, or `percent × budget` when derivable. */
  amountUzs: number | null;
}

/** Tolerates the plain decimal strings the API sends ("3", "300000") and the
 *  space-grouped ones some callers already hold. */
function toFiniteNumber(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/\s/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * Split a stored bid security into its two possible readings — see the note on
 * `BID_SECURITY_PERCENT_CEILING`. `null` when there is nothing to show (absent,
 * zero or unparseable; a zero deposit is not a real requirement).
 *
 * `publishedPct` is the API's `bidSecurityPct`: the RATE the source itself
 * published (etender / ebirja / most xt-xarid lots). Since 2026-09-29 the API
 * stores those lots' deposit in so'm — `round(budget × rate / 100)` — so
 * without the rate a «3 %» lot would print our arithmetic as if the source
 * had published that sum. When it is given, the rate leads and the stored
 * amount (or, for a row not yet converted, budget × rate) is the «≈» half.
 */
export function parseBidSecurity(
  raw: string | number | null | undefined,
  budgetUzs?: string | number | null,
  publishedPct?: number | null,
): BidSecurity | null {
  const pct = toFiniteNumber(publishedPct);
  // ≤ 100, the same bound the API's `pledgePercentOf` applies.
  if (pct != null && pct > 0 && pct <= BID_SECURITY_PERCENT_CEILING) {
    const stored = toFiniteNumber(raw);
    const budget = toFiniteNumber(budgetUzs);
    return {
      percent: pct,
      amountUzs:
        stored != null && stored >= BID_SECURITY_PERCENT_CEILING
          ? stored
          : budget != null && budget > 0
            ? (budget * pct) / 100
            : null,
    };
  }

  const value = toFiniteNumber(raw);
  if (value == null || value <= 0) return null;
  if (value >= BID_SECURITY_PERCENT_CEILING) return { percent: null, amountUzs: value };
  const budget = toFiniteNumber(budgetUzs);
  return {
    percent: value,
    amountUzs: budget != null && budget > 0 ? (budget * value) / 100 : null,
  };
}

/**
 * The VALUE half of a bid-security row — «300 000 сум» when the source
 * published a sum, «3 %» when it published a rate. The label («Обеспечение
 * заявки») stays with the caller, which owns the layout.
 *
 * `unit` is the caller's `t('tender.budget.unit')` so this file needs no i18n.
 *
 * ## `approxAmount` — one rule per SURFACE, never per lot
 *
 * The DETAIL page (and only it) also spells out what the rate works out to:
 * «3 % ≈ 8,1 млн сум». Cards do not — «Обеспечение заявки: 1 % ≈ 12 млн сум»
 * wrapped to two lines on a phone and buried the budget it sits next to
 * (Igor, 2026-09-11).
 *
 * ⚠️ The switch is a per-surface CONSTANT on purpose. The tempting version —
 * "append the sum when there is room" — is a width check, which means the same
 * lot reads differently on two phones and no screenshot can be trusted as
 * evidence of what a user saw. One surface, one rule.
 *
 * The derived amount is always compact AND always prefixed «≈»: it is our
 * arithmetic, not a figure the source published, and must never be mistaken
 * for one.
 */
export function formatBidSecurity(
  raw: string | number | null | undefined,
  budgetUzs: string | number | null | undefined,
  locale: string,
  unit: string,
  opts?: {
    compact?: boolean;
    approxAmount?: boolean;
    /** The API's `bidSecurityPct` — see `parseBidSecurity`. Pass it on every
     *  surface: without it a converted «3 %» lot prints a bare sum. */
    publishedPct?: number | null;
  },
): string | null {
  const bs = parseBidSecurity(raw, budgetUzs, opts?.publishedPct);
  if (bs == null) return null;

  if (bs.percent == null) {
    const amount = opts?.compact
      ? formatBudgetCompact(String(bs.amountUzs), locale)
      : formatBudget(String(bs.amountUzs));
    return amount == null ? null : withCurrency(amount, unit);
  }

  const percent = `${formatDecimal(bs.percent, locale)} %`;
  if (!opts?.approxAmount || bs.amountUzs == null) return percent;
  const approx = formatBudgetCompact(String(Math.round(bs.amountUzs)), locale);
  return approx == null ? percent : `${percent} ≈ ${withCurrency(approx, unit)}`;
}

/**
 * "1 sentabr 2026, 17:00" / "1 сентября 2026, 17:00" — a timestamp for the
 * «Данные на …» line.
 *
 * Shares `formatDeadline`'s month handling, so it inherits the Uzbek fix rather
 * than reaching for `toLocaleString(locale)`, which in Chrome renders `uz` as
 * the bare ISO-ish "2026-09-01 17:00:00" (root fallback again).
 */
export function formatDateTime(iso: string | null | undefined, locale: string): string | null {
  if (!iso) return null;
  const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(SITE_TIME_ZONE);
  if (!dt.isValid) return null;
  const date = formatDeadline(dt.toISODate(), locale);
  return date == null ? null : `${date}, ${dt.toFormat('HH:mm')}`;
}

/**
 * "05:00" — the clock time an ISO instant lands on in Tashkent ({@link
 * SITE_TIME_ZONE}: Madrid on ES, where midnight UTC reads "02:00"/"01:00").
 *
 * Used by copy that has to name a MOMENT rather than a day: a read ceiling
 * resets at 00:00 UTC, and telling a Tashkent reader that is "tomorrow" is
 * wrong for the five hours in between.
 *
 * ⚠️ `toFormat('HH:mm')` on purpose, never `toLocaleString(locale)`. Chrome
 * ships no `uz` CLDR data, so anything that routes through `Intl` renders as
 * the root fallback for half our users while passing every Node test — the same
 * trap `formatDeadline` and `formatDateTime` above exist to avoid. A 24-hour
 * clock is locale-neutral, so there is nothing to localize here anyway.
 *
 * Zone {@link SITE_TIME_ZONE}, matching `formatDateTime` and the /plans
 * renewal dates. Null for an absent or unparseable instant.
 */
export function formatTashkentTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(SITE_TIME_ZONE);
  return dt.isValid ? dt.toFormat('HH:mm') : null;
}

/**
 * "27.09.2026" — the calendar day an ISO instant falls on IN TASHKENT (in
 * {@link SITE_TIME_ZONE}: Madrid on ES).
 *
 * The zoned twin of {@link formatDateNumeric}, which reads the date in UTC and
 * so names the previous day for anything stamped between 00:00 and 05:00
 * Tashkent time — a package built at 02:00 on the 28th would read «собран
 * 27.09». Same `toFormat`, never `Intl`, for the reason `formatTashkentTime`
 * gives.
 */
export function formatTashkentDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(SITE_TIME_ZONE);
  return dt.isValid ? dt.toFormat(NUMERIC_DATE) : null;
}

/**
 * Whole days from now until an ISO deadline (ceil, so "today" reads as the last
 * day, not 0). Negative/invalid/absent → null. Uses luxon (no `new Date`). The
 * value is computed at render time; in prerendered HTML it reflects build time
 * and is corrected on hydration — fine for a soft countdown badge.
 */
export function daysLeft(deadline: string | null | undefined): number | null {
  if (!deadline) return null;
  const dt = DateTime.fromISO(deadline, { zone: 'utc' });
  if (!dt.isValid) return null;
  const diff = Math.ceil(dt.diff(DateTime.utc(), 'days').days);
  return diff >= 0 ? diff : null;
}

/** Localized noun for "day(s)" agreeing with `n` — Russian has the 1/2-4/5+
 *  three-form plural; Uzbek ("kun") and Kazakh ("күн") are invariant after a
 *  numeral. Pairs with `daysLeft`. Kazakh fell through to Russian until
 *  2026-10-16: the /kk lot page read «МЕРЗІМ 0 дней». */
export function daysWord(n: number, locale: string): string {
  if (locale === 'uz') return 'kun';
  if (locale === 'kk') return 'күн';
  if (locale === 'es') return n === 1 ? 'día' : 'días';
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'день';
  if (m10 >= 2 && m10 <= 4 && !(m100 >= 12 && m100 <= 14)) return 'дня';
  return 'дней';
}

/**
 * Russian's three-form plural, generalised — the same 1 / 2-4 / 5+ rule
 * `daysWord` implements for days.
 *
 * Interpolating a count into a fixed noun produces «Нашли 2 тендеров», which is
 * the plural form for five or more. It is the kind of error a native reader
 * notices immediately and a template never will, and it appeared the moment a
 * real count reached the screen.
 *
 * Uzbek needs none of this: its nouns do not agree with a preceding numeral, so
 * callers pass the single form and it is returned unchanged.
 */
export function pluralRu(n: number, one: string, few: string, many: string): string {
  return [one, few, many][pluralRuIndex(n)]!;
}

/**
 * The same 1 / 2-4 / 5+ rule as {@link pluralRu}, but returning an INDEX — for
 * callers whose three forms live in the locale files as `…one` / `…few` /
 * `…many` keys rather than as literals in code.
 *
 * One rule, one place: the concentration card picks `supplierGraph.contracts.*`
 * with it and the teaser line picks `supplierGraph.suppliers.*`, so «583
 * контрактов · 34 поставщиков» (the genitive plural applied to every count)
 * cannot come back on one surface while the other is correct. Uzbek and Kazakh
 * keys are identical across the three forms, so the index is harmless there.
 */
export function pluralRuIndex(n: number): 0 | 1 | 2 {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 0;
  if (m10 >= 2 && m10 <= 4 && !(m100 >= 12 && m100 <= 14)) return 1;
  return 2;
}

/**
 * The `.one` / `.few` / `.many` KEY index for a count, in the language of the
 * deployment's catalogue — for callers whose forms live in the locale files.
 *
 * Russian's 1 / 2-4 / 5+ rule ({@link pluralRuIndex}) everywhere it always
 * applied (Uzbek and Kazakh keys are identical across the three forms). Spanish
 * has two forms — one for 1, the other for everything else — and its catalogue
 * puts the plural in `.few` and `.many`, so the Russian rule would pick the
 * singular for 21, 31, 41… Spanish is the ES deployment's only locale (and no
 * other deployment serves it), so this reads the deployment rather than taking a
 * locale argument every caller would have to thread through.
 */
export function pluralKeyIndex(n: number): 0 | 1 | 2 {
  if (countryConfig.locales.includes('es')) return n === 1 ? 0 : 2;
  return pluralRuIndex(n);
}

/** «тариф / тарифа / тарифов». Live case: production offers 5 paid plans, so
 *  the hardcoded few-form rendered «5 тарифа». */
export function plansWord(n: number, locale: string): string {
  if (locale === 'kk') return 'тариф';
  if (locale === 'es') return n === 1 ? 'plan' : 'planes';
  return locale === 'uz' ? 'tarif' : pluralRu(n, 'тариф', 'тарифа', 'тарифов');
}

/** «контакт / контакта / контактов» — the ИП allowance is data-driven, so the
 *  noun beside it has to be too. Correct at 5 today; 2 would have read
 *  «2 контактов». */
export function contactsWord(n: number, locale: string): string {
  if (locale === 'kk') return 'байланыс';
  if (locale === 'es') return n === 1 ? 'contacto' : 'contactos';
  return locale === 'uz' ? 'kontakt' : pluralRu(n, 'контакт', 'контакта', 'контактов');
}

/** «тендер / тендера / тендеров» for a count, invariant in Uzbek and Kazakh
 *  (neither inflects after a numeral). Live case (2026-09-30): the results
 *  line read «3771 тендеров» and «92 тендеров» — the genitive plural on every
 *  number — because `result.count` carried the word as a literal. */
export function tendersWord(n: number, locale: string): string {
  if (locale === 'uz') return 'tender';
  if (locale === 'kk') return 'тендер';
  if (locale === 'es') return n === 1 ? 'licitación' : 'licitaciones';
  return pluralRu(n, 'тендер', 'тендера', 'тендеров');
}

/** «лот / лота / лотов» for a count; invariant in Uzbek («lot») and Kazakh
 *  («лот»). The «other lots of this announcement» heading used «лот(ов)». */
export function lotsWord(n: number, locale: string): string {
  if (locale === 'uz') return 'lot';
  if (locale === 'kk') return 'лот';
  if (locale === 'es') return n === 1 ? 'lote' : 'lotes';
  return pluralRu(n, 'лот', 'лота', 'лотов');
}

/**
 * Format an Uzbek phone number for display: "998977888385" → "+998 97 788-83-85".
 *
 * Accepts the raw stored value in whatever shape the source gave it (with or
 * without a leading +, 998 country code, spaces or dashes) and normalizes to the
 * canonical UZ grouping. Handles the two real shapes: a 12-digit number with the
 * 998 country code, and a bare 9-digit local number (2-digit operator + 7). Any
 * other length is returned trimmed-as-is rather than mis-grouped — a landline or
 * an unexpected format is shown verbatim, never mangled. The `tel:` href should
 * keep using the RAW value; this is display-only.
 */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  if (countryConfig.code === 'es') return formatEsPhone(raw);
  if (CHILE_EDITION) return formatClPhone(raw);
  let d = raw.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('998')) d = d.slice(3);
  else if (d.length !== 9) return raw.trim();
  // d is now 9 digits: OO NNN NN NN
  return `+998 ${d.slice(0, 2)} ${d.slice(2, 5)}-${d.slice(5, 7)}-${d.slice(7, 9)}`;
}

/**
 * Spain: «+34 912 345 678» for a nine-digit national number, with or without
 * the 34 / 0034 prefix. Anything else (an extension, a foreign number) is shown
 * as given — never re-grouped under the Uzbek mask, which is what a bare nine-
 * digit Spanish number would otherwise get.
 */
function formatEsPhone(raw: string): string {
  let d = raw.replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('0034')) d = d.slice(4);
  else if (d.length === 11 && d.startsWith('34')) d = d.slice(2);
  if (d.length !== 9) return raw.trim();
  return `+34 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 9)}`;
}

/**
 * Chile: «+56 9 1234 5678» for a celular, «+56 2 2345 6789» for a Santiago line
 * and «+56 32 234 5678» for another area code — the nine-digit national number
 * (since 2016), with or without the 56 / 0056 prefix. Anything else is shown as
 * given, never re-grouped under another country's mask.
 */
export function formatClPhone(raw: string): string {
  let d = raw.replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('0056')) d = d.slice(4);
  else if (d.length === 11 && d.startsWith('56')) d = d.slice(2);
  if (!/^[2-9]\d{8}$/.test(d)) return raw.trim();
  if (d[0] === '9' || d[0] === '2') return `+56 ${d[0]} ${d.slice(1, 5)} ${d.slice(5)}`;
  return `+56 ${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5)}`;
}

/**
 * The Chile phone FIELD mask (sign-up): any input → `+56 9 1234 5678` as it is
 * typed (a Santiago line `+56 2 2345 6789`, another area code `+56 32 234 5678`).
 * Nine national digits, no trunk prefix; a paste may carry `56` or `0056` in
 * front, which only counts as the country code when the field did not already
 * show our own `+56 `. The Spanish and Kazakh masks live in `routes/register.tsx`.
 */
export function formatClPhoneInput(raw: string): string {
  const hadPrefix = /^\s*\+56\s/.test(raw);
  let digits = raw.replace(/^\s*\+56\s*/, '').replace(/\D/g, '');
  if (!hadPrefix && digits.length >= 13 && digits.startsWith('0056')) digits = digits.slice(4);
  else if (!hadPrefix && digits.length >= 11 && digits.startsWith('56')) digits = digits.slice(2);
  digits = digits.slice(0, 9);
  if (digits.length === 0) return '';
  const head = digits[0] === '9' || digits[0] === '2' ? 1 : 2;
  const groups = [digits.slice(0, head), digits.slice(head, 5), digits.slice(5)].filter(Boolean);
  return `+56 ${groups.join(' ')}`;
}

/**
 * Chile writes the peso sign BEFORE the amount, after a space: «$ 1.234.567»,
 * «$ 1,2 M» (Liciora). Everywhere else the unit follows the amount, as it always
 * did («1 234 567 сум», «49 €»). The space before the amount is a no-break one,
 * so a line never ends on a lone «$».
 */
export const CURRENCY_FIRST = CHILE_EDITION;

/** An amount and its currency unit in this deployment's order — see {@link CURRENCY_FIRST}. */
export function withCurrency(amount: string | number | null | undefined, unit: string): string {
  return CURRENCY_FIRST ? `${unit}\u00A0${amount}` : `${amount} ${unit}`;
}

/**
 * Space-grouped integer for headline counters ("3 100", "243 713") — landing
 * stat tiles, not currency. Deliberately not `Intl.NumberFormat`/
 * `toLocaleString`, for the same reason as {@link formatBudgetCompact}: Chrome
 * ships no `uz` CLDR grouping data. Unlike the budget formatters this has no
 * locale-specific branch — both site locales group with a plain space, so
 * there's nothing to switch on.
 */
export function formatCount(n: number): string {
  // ES (`numberLocale`): «3.100», the locale's own group separator.
  const numberLocale = countryConfig.currency.numberLocale;
  if (numberLocale) return formatLocaleAmount(String(Math.round(n)), numberLocale) ?? '0';
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/**
 * A share as the page prints it, one decimal: «12.5%» on UZ and KZ — the
 * `toFixed(1)` the cards always printed — and «12,5 %» on Spain, the comma and
 * the space Spanish writes (the buyer/supplier cards printed «41.7%», 2026-10-21).
 */
export function formatPct(value: number, digits = 1): string {
  const n = formatShareNumber(value, digits);
  return HISPANIC_EDITION ? `${n}\u00A0%` : `${n}%`;
}

/**
 * A 12-month stat tile's percentage («Mediana de la baja», «Con un solo licitador»):
 * exactly what UZ and KZ always printed (`toLocaleString` ru/uz, «12,5%»), and es-ES
 * with the space Spanish writes on Spain («12,5 %»; it borrowed the ru format before).
 */
export function pctStat(value: number, locale: string): string {
  if (HISPANIC_EDITION) {
    return `${value.toLocaleString(CHILE_EDITION ? 'es-CL' : 'es-ES')}\u00A0%`;
  }
  return `${value.toLocaleString(locale === 'uz' ? 'uz' : 'ru')}%`;
}

/**
 * A percentage the API already rounded («12.5», «5»), printed as sent: `12.5%` on UZ
 * and KZ, as always; `12,5 %` on Spain («Baja mediana» on the lot page).
 */
export function formatPctAsSent(value: number): string {
  return HISPANIC_EDITION ? `${String(value).replace('.', ',')}\u00A0%` : `${value}%`;
}

/** The number half of {@link formatPct}, for copy that writes the «%» itself. */
export function formatShareNumber(value: number, digits = 1): string {
  const fixed = value.toFixed(digits);
  return HISPANIC_EDITION ? fixed.replace('.', ',') : fixed;
}

/**
 * Whether a contract row's amount is worth printing. Always on UZ and KZ, which
 * keep their «0 сум»; on Spain an amount under one euro is a placeholder the buyer
 * typed (a framework agreement's «0», a «0,04» expediente total) and printed «0 €»
 * on the lot rail and the company pages (QA 2026-10-21), so the row shows none.
 */
export function amountShown(value: number | string | null | undefined): boolean {
  if (!HISPANIC_EDITION) return true;
  const n = typeof value === 'string' ? Number(value) : value;
  return n != null && Number.isFinite(n) && n >= 1;
}

/**
 * A headline count as the page prints it: grouped on a deployment with its own
 * `numberLocale` (Spain: «12.878 licitaciones», not «12878»), and the bare
 * number everywhere else — UZ/KZ interpolate exactly what they did before.
 */
export function localeCount(n: number): number | string {
  return countryConfig.currency.numberLocale ? formatCount(n) : n;
}

/**
 * Normalize an API status into one of the two m16 visual statuses. Anything
 * that isn't explicitly "closing" renders as the calm "active" treatment.
 */
/**
 * Badge state for a tender's lifecycle.
 *
 * This used to collapse to `'active' | 'closing'`, so EVERY finished lot —
 * closed, expired, archived — rendered as "Активен"/"Faol". Harmless while the
 * catalog only showed live lots, but awarded-outcome pages are now indexed and
 * public, and they showed "Active" directly above "Итог закупки: Победитель …
 * final price". A page that contradicts itself in two adjacent blocks is a
 * credibility problem for a searcher and for a quality rater, and it is exactly
 * the sort of detail an AI engine reads literally when deciding what to cite.
 */
export function statusKind(status: string): 'active' | 'closing' | 'closed' {
  if (status === 'closing') return 'closing';
  if (status === 'closed' || status === 'expired' || status === 'archived') return 'closed';
  return 'active';
}
